import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormContext } from "react-hook-form";

import type { TaxCode } from "@/apis/gql/types";
import {
  ComboBox,
  type ComboOption,
} from "@/components/shared/tables/tableComboBox";

type Props = {
  name: string;
  label?: string;
  options: TaxCode[];
  autoFocus?: boolean;
  placeholder?: string;
  description?: string;
  fullWidth?: boolean;
  variant?: "outline" | "ghost";
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  sideOffset?: number;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  specialNotice?: React.ReactNode;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement | HTMLInputElement>,
  ) => void;
  modal?: boolean;
  enableClear?: boolean;
};

export const TaxCodeDropdownInput = ({
  label,
  name,
  options,
  fullWidth = true,
  placeholder,
  description,
  specialNotice,
  className,
  sideOffset,
  variant = "outline",
  readOnly = false,
  disabled = false,
  onKeyDown,
  modal = false,
  enableClear = false,
}: Props) => {
  const form = useFormContext();

  const items =
    options?.map(
      (taxCode): ComboOption => ({
        label: taxCode.name,
        alternativeLabels: [taxCode.datevBu],
        value: taxCode.taxCodeId,
        subLabel: taxCode.datevBu,
      }),
    ) ?? [];

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // calculate the current value
        const currentOption = options.find(
          (option) => option.taxCodeId === field.value,
        );

        const value = currentOption
          ? {
              label: currentOption.name,
              value: currentOption.taxCodeId,
              subLabel: currentOption.datevBu,
              alternativeLabels: [],
            }
          : undefined;

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <div className="flex flex-col gap-0.5 min-w-0">
                <ComboBox
                  currentOption={value}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  items={items}
                  fullWidth={fullWidth}
                  placeholder={placeholder}
                  emptyLabel="Keinen BU gefunden"
                  variant={variant}
                  className={className}
                  sideOffset={sideOffset}
                  readOnly={readOnly}
                  disabled={disabled}
                  onKeyDown={onKeyDown}
                  modal={modal}
                  enableClear={enableClear}
                />
                {specialNotice && (
                  <div className="text-xs text-gray-500 text-left mt-0.5 w-full min-w-0 overflow-hidden">
                    {specialNotice}
                  </div>
                )}
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
